import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_CONFIG } from '../config/api';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, Calendar, Tag, User, Clock, BookOpen,
  Brain, Home, Eye, ThumbsUp, TrendingUp, Award, ChevronRight,
  Target, Zap, CheckCircle, Star
} from 'lucide-react';
import SEOHead from '../components/common/SEOHead';
import { ResponsiveLogo } from '../components/common/Logo';
import { useAuth } from '../contexts/AuthContext';
import UnifiedNavigation from '../components/common/UnifiedNavigation';
import BreadcrumbNavigation from '../components/common/BreadcrumbNavigation';
import MarkdownRenderer from '../components/diagnosis/MarkdownRenderer';
import EnhancedHTMLRenderer from '../components/analysis/EnhancedHTMLRenderer';
import DeepAnalysisDisplay from '../components/analysis/DeepAnalysisDisplay';
import { supabase, isAdmin } from '../lib/supabase';

// 新增UX改进组件导入
// import NewsContextSection from '../components/analysis/NewsContextSection'; // 已移除
import LearningPathSection from '../components/analysis/LearningPathSection';
// 移除FloatingShareTools导入
import ProgressiveActionGuide from '../components/analysis/ProgressiveActionGuide';
import ActionZone from '../components/analysis/ActionZone';
import ThinkingProcessAnalyzer from '../components/analysis/ThinkingProcessAnalyzer';
import { NewsArticleService } from '../services/newsArticleService';
import { 
  NewsArticle, LearningProgress, AvailableAction,
  UserJourneyStage, EngagementMetrics
} from '../types/enhancedAnalysis';
// NewsAnalysisRelation 已移除

interface OfficialAnalysis {
  id: number;
  article_id: number;
  template_id: number;
  author_id: number;
  content_json: any;
  full_article?: string;
  think_content?: string;
  slug?: string;
  status: number;
  created_at: string;
  updated_at: string;
  title: string;
  summary: string;
  category: string;
  template_name: string;
  preview_content: string;
}

const OfficialAnalysisPreview: React.FC = () => {
  const { slug, id } = useParams<{ slug?: string; id?: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [analysis, setAnalysis] = useState<OfficialAnalysis | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // 移除 activeTab 状态，不再需要标签页

  // 新增UX改进状态
  const [newsArticle, setNewsArticle] = useState<NewsArticle | null>(null);
  // const [newsAnalysisRelation, setNewsAnalysisRelation] = useState<NewsAnalysisRelation | null>(null); // 已移除
  const [readingProgress, setReadingProgress] = useState(0);
  // const [newsExpanded, setNewsExpanded] = useState(false); // 已移除
  const [currentUserStage, setCurrentUserStage] = useState<UserJourneyStage>('reading');
  const [learningProgress, setLearningProgress] = useState<LearningProgress>({
    hasViewedMethodology: false,
    hasDownloadedTemplate: false,
    hasStartedPractice: false,
    completionPercentage: 0,
    nextRecommendedAction: {
      id: 'view_methodology',
      title: '查看分析方法',
      description: '了解这篇分析使用的方法论',
      type: 'read',
      priority: 'high',
      estimatedTime: 10,
      requiresAuth: false
    },
    learningPath: []
  });

  // Refs
  const contentRef = useRef<HTMLDivElement>(null);


  // 获取官方深度分析数据
  useEffect(() => {
    const fetchAnalysis = async () => {
    if (!slug && !id) {
      setError('缺少分析参数');
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      
      // 使用edge function替代直接API调用
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      
      let data = null;
      
      // 首先尝试使用专用的get-official-analysis-detail函数
      try {
        // 构建查询参数
        const params = new URLSearchParams();
        if (slug) {
          params.append('slug', slug);
        } else if (id) {
          params.append('id', id);
        }
        
        // 调用edge function
        const functionsUrl = API_CONFIG.FUNCTIONS_URL || `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
        console.log('🔍 尝试调用专用API:', `${functionsUrl}/public-get-official-analysis-detail?${params}`);
        
        // 构建请求头，确保总是有授权信息
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
        };
        
        // 如果用户已登录，使用用户token；否则使用anon key作为Bearer token
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        } else {
          headers['Authorization'] = `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`;
        }
        
        const response = await fetch(`${functionsUrl}/public-get-official-analysis-detail?${params}`, {
          method: 'GET',
          headers,
        });
        
        if (response.ok) {
          const result = await response.json();
          console.log('🎯 专用API响应结果:', result);
          
          if (result.success) {
            data = result.data;
          }
        } else {
          console.warn('🚨 专用API调用失败，尝试备用方案');
        }
      } catch (error) {
        console.warn('🚨 专用API调用异常，尝试备用方案:', error);
      }
      
      // 如果专用API失败，抛出错误
      if (!data) {
        throw new Error('无法获取分析数据，请稍后重试');
      }
      
      if (!data) {
        throw new Error('获取分析数据失败');
      }
      
      console.log('🔍 获取到的分析数据:', data);
      console.log('🧠 content_json 内容:', data?.content_json);
      console.log('💭 thinking_process 内容:', data?.content_json?.thinking_process);
      
      setAnalysis(data);
      
      // 直接使用公开接口返回的新闻数据，避免重复调用管理接口
      if (data?.insight_news_article) {
        const newsData = data.insight_news_article;
        const transformedNewsArticle = {
          id: data.id?.toString() || 'unknown',
          title: newsData.title || '',
          summary: newsData.summary || '',
          content: newsData.content || '',
          category: newsData.category || '',
          originalUrl: newsData.original_url,
          publishTime: new Date(),
          readingTime: Math.ceil((newsData.content?.length || 0) / 300),
          source: {
            id: 'public-source',
            name: '官方新闻',
            credibilityScore: 0.9,
            type: 'official' as const
          },
          tags: [],
          status: 1,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        setNewsArticle(transformedNewsArticle);
      }
    } catch (err) {
      console.error('获取官方深度分析失败:', err);
      setError('获取分析失败');
    } finally {
      setIsLoading(false);
    }
    };

    fetchAnalysis();
  }, [slug, id]);

  // 注释：已优化为直接使用公开接口返回的新闻数据，不再需要额外调用管理接口
  // const loadNewsArticle = useCallback(async (analysisId: string) => {
  //   // 此函数已被优化移除，避免重复调用 news-admin-manage 接口
  // }, []);

  // 滚动监听 - 更新阅读进度
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = Math.min((scrollTop / docHeight) * 100, 100);

      setReadingProgress(progress);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 所有事件处理器都放在这里，确保 hooks 顺序一致，必须在早期返回之前
  const handleCaseAnalysisClick = useCallback(() => {
    navigate('/analyses');
  }, [navigate]);

  const handleTryTemplate = useCallback(() => {
    if (user) {
      navigate(`/training?template=${analysis?.id}`);
    } else {
      navigate('/');
    }
  }, [user, navigate, analysis?.id]);

  // const handleNewsToggle = useCallback(() => {
  //   setNewsExpanded(!newsExpanded);
  // }, [newsExpanded]);

  // const handleViewOriginal = useCallback(() => {
  //   if (newsArticle?.originalUrl) {
  //     window.open(newsArticle.originalUrl, '_blank');
  //   }
  // }, [newsArticle]); // 已移除

  const handleStartLearning = useCallback(() => {
    setLearningProgress(prev => ({
      ...prev,
      hasViewedMethodology: true
    }));
  }, []);

  const handleViewResources = useCallback(() => {
    console.log('View resources');
  }, []);

  // 移除分享处理函数

  const handleContinueLearning = useCallback(() => {
    handleStartLearning();
  }, [handleStartLearning]);

  const handleExploreMore = useCallback(() => {
    navigate('/analyses');
  }, [navigate]);

  const handleActionSelect = useCallback((action: AvailableAction) => {
    switch (action.id) {
      case 'start_practice':
        handleTryTemplate();
        break;
      case 'view_methodology':
        handleStartLearning();
        break;
      case 'register':
        navigate('/');
        break;
      default:
        console.log('Action selected:', action);
    }
  }, [handleTryTemplate, handleStartLearning, navigate]);

  const handleActionDismiss = useCallback(() => {
    console.log('Action guide dismissed');
  }, []);

  // 早期返回必须在所有 hooks 之后
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📄</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">分析不存在</h2>
          <p className="text-gray-600 mb-4">抱歉，找不到您要查看的官方深度分析。</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            返回首页
          </button>
        </div>
      </div>
    );
  }

  const seoNewsArticle = {
    headline: analysis.title,
    description: analysis.summary,
    author: { name: '思维训练平台', url: window.location.origin },
    publisher: { name: '思维训练平台', url: window.location.origin, logo: `${window.location.origin}/logo.png` },
    datePublished: analysis.created_at,
    url: `${window.location.origin}/official-analysis/${analysis.slug || analysis.id}`,
  };

  const creativeWork = {
    name: analysis.title,
    description: analysis.summary,
    author: { name: '思维训练平台', url: window.location.origin },
    publisher: { name: '思维训练平台', url: window.location.origin, logo: `${window.location.origin}/logo.png` },
    dateCreated: analysis.created_at,
    url: `${window.location.origin}/official-analysis/${analysis.slug || analysis.id}`,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={analysis.title}
        description={analysis.summary}
        newsArticle={seoNewsArticle}
        creativeWork={creativeWork}
      />
      
      {/* 顶部导航栏 */}
      <UnifiedNavigation currentPage="analyses" />

      {/* 页面内容区域 */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 面包屑导航 */}
        <BreadcrumbNavigation 
          items={[
            { name: '案例分析', href: '/analyses' },
            { name: analysis.title, current: true }
          ]}
          className="mb-6"
        />

        {/* 标题与摘要 - 移到新闻背景上面 */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-2 leading-tight">
            {analysis.title}
          </h1>
          {analysis.summary && (
            <p className="text-xl text-gray-600 leading-relaxed mb-4">
              {analysis.summary}
            </p>
          )}

          {/* 元信息 */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center">
              <Calendar className="w-4 h-4 mr-1" />
              {new Date(analysis.created_at).toLocaleDateString()}
            </div>
            <div className="flex items-center">
              <Tag className="w-4 h-4 mr-1" />
              {analysis.category}
            </div>
            <div className="flex items-center">
              <Clock className="w-4 h-4 mr-1" />
              约{Math.ceil((analysis.full_article || analysis.preview_content).length / 300)}分钟阅读
            </div>
            <div className="flex items-center">
              <Eye className="w-4 h-4 mr-1" />
              官方深度分析
            </div>
          </div>

          {/* 查看原文链接 */}
          {newsArticle && newsArticle.originalUrl && (
            <div className="mt-4">
              <a
                href={newsArticle.originalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                查看原文
                <ChevronRight className="w-4 h-4 ml-1" />
              </a>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* 主要内容区域 */}
          <div className="lg:col-span-3" ref={contentRef}>
            {/* 新闻背景区域已移除 */}

            {/* 使用新的深度分析展示组件 */}
            <div className="bg-white rounded-lg shadow-sm p-8">
              <DeepAnalysisDisplay
                analysis={{
                  full_article: analysis.full_article,
                  preview_content: analysis.preview_content,
                  content_json: analysis.content_json
                }}
                title="AI深度分析"
                showThinkingProcess={true}
              />
            </div>



            {/* 转化区域 */}
            <ActionZone
              analysis={{
                id: analysis.id.toString(),
                slug: analysis.slug || '',
                title: analysis.title,
                summary: analysis.summary,
                preview_content: analysis.preview_content,
                full_article: analysis.full_article,
                category: analysis.category,
                template_name: analysis.template_name,
                created_at: analysis.created_at,
                updated_at: analysis.updated_at,
                author: {
                  id: 'system',
                  name: '思维训练平台',
                  title: '深度分析专家',
                  expertise: ['商业分析', '战略思维', '数据洞察']
                },
                readingTime: 15,
                difficultyLevel: 'intermediate' as const,
                wordCount: analysis.summary.length * 5,
                lastUpdated: analysis.updated_at || analysis.created_at,
                methodology: {
                  id: 'default',
                  framework: analysis.template_name || '综合分析',
                  name: analysis.template_name || '综合分析方法',
                  description: '基于多维度的系统性分析方法',
                  steps: [],
                  relatedResources: [],
                  estimatedTime: 30,
                  difficulty: 'intermediate' as const
                },
                keyInsights: [
                  {
                    id: '1',
                    title: '核心洞察',
                    description: analysis.summary,
                    importance: 'high' as const,
                    category: analysis.category,
                    shareableQuote: analysis.summary.slice(0, 100) + '...',
                    actionable: true
                  }
                ],
                practicalTips: [],
                socialMetrics: {
                  views: 0,
                  likes: 0,
                  comments: 0,
                  bookmarks: 0,
                  engagementRate: 0,
                  trending: false
                },
                seoMetadata: {
                  title: analysis.title,
                  description: analysis.summary,
                  keywords: [analysis.category],
                  canonicalUrl: '',
                  structuredData: {
                    type: 'Article' as const,
                    headline: analysis.title,
                    description: analysis.summary,
                    author: {
                      id: 'system',
                      name: '思维训练平台',
                      title: '深度分析专家',
                      expertise: ['商业分析', '战略思维', '数据洞察']
                    },
                    datePublished: analysis.created_at,
                    dateModified: analysis.updated_at || analysis.created_at,
                    publisher: {
                      name: '思维训练平台',
                      logo: ''
                    },
                    mainEntityOfPage: ''
                  },
                  breadcrumbs: []
                },
                relatedCases: [],
                recommendedActions: [
                  {
                    id: '1',
                    title: '尝试分析',
                    description: '使用相同的方法论分析其他案例',
                    type: 'practice' as const,
                    priority: 'high' as const,
                    estimatedTime: 30,
                    requiresAuth: true
                  }
                ],
                tableOfContents: [],
                sections: []
              }}
              newsArticle={newsArticle ?? undefined}
              userEngagement={{
                sessionId: '',
                analysisId: analysis.id.toString(),
                readingProgress: readingProgress,
                timeSpent: 0,
                scrollDepth: readingProgress,
                readingSpeed: 0,
                sectionsViewed: ['analysis'],
                methodologyViewed: false,
                // 移除shareAttempts字段
                bookmarked: false,
                liked: false,
                commented: false,
                practiceStarted: learningProgress.hasStartedPractice,
                templateDownloaded: learningProgress.hasDownloadedTemplate,
                relatedCasesViewed: 0,
                resourcesAccessed: [],
                registrationPrompted: false,
                registrationCompleted: !!user,
                conversionValue: 0,
                startTime: new Date(),
                lastActiveTime: new Date(),
                deviceType: 'desktop'
              }}
              user={user}
              onRegister={() => navigate('/')}
              onContinueLearning={handleContinueLearning}
              onExploreMore={handleExploreMore}
              readingProgress={readingProgress}
            />
          </div>

          {/* 侧边栏 */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              {/* 分析统计 */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4">分析统计</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">字数统计</span>
                    <span className="text-sm font-medium text-gray-900">{(analysis.full_article || analysis.preview_content).length}字</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">阅读时长</span>
                    <span className="text-sm font-medium text-gray-900">约{Math.ceil((analysis.full_article || analysis.preview_content).length / 300)}分钟</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">阅读进度</span>
                    <span className="text-sm font-medium text-gray-900">{Math.round(readingProgress)}%</span>
                  </div>
                </div>
              </div>

              {/* 移除浮动分享工具 */}

              {/* 您的学习收获 */}
              <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
                <h3 className="font-semibold text-gray-900 mb-4 flex items-center">
                  <Award className="w-5 h-5 text-yellow-500 mr-2" />
                  您的学习收获
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">掌握的分析方法</h4>
                    <div className="flex items-center space-x-2 text-sm text-gray-600">
                      <Zap className="w-4 h-4 text-blue-500" />
                      <span>{analysis.template_name || '分析方法'}</span>
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">关键洞察</h4>
                    <div className="text-sm text-gray-600">
                      获得了多个深度洞察
                    </div>
                  </div>
                  
                  {newsArticle && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">新闻理解</h4>
                      <div className="text-sm text-gray-600">
                        深入理解了"{newsArticle?.title?.slice(0, 30) || '相关新闻'}..."的背景和影响
                      </div>
                    </div>
                  )}
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">思维提升</h4>
                    <div className="text-sm text-gray-600">
                      提升了{analysis.template_name || '系统性'}分析思维能力
                    </div>
                  </div>
                </div>
              </div>

              {/* 相关标签 */}
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="font-semibold text-gray-900 mb-4">相关标签</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">{analysis.category}</span>
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full">官方分析</span>
                  <span className="px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full">案例研究</span>
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm rounded-full">思维训练</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
      </div>

      {/* 渐进式操作引导 */}
      <ProgressiveActionGuide
        currentStage={currentUserStage}
        readingProgress={readingProgress}
        engagementMetrics={{
          sessionId: '',
          analysisId: analysis.id.toString(),
          readingProgress: readingProgress,
          timeSpent: 0,
          scrollDepth: readingProgress,
          readingSpeed: 0,
          sectionsViewed: ['analysis'],
          methodologyViewed: false,
          // 移除shareAttempts字段
          bookmarked: false,
          liked: false,
          commented: false,
          practiceStarted: learningProgress.hasStartedPractice,
          templateDownloaded: learningProgress.hasDownloadedTemplate,
          relatedCasesViewed: 0,
          resourcesAccessed: [],
          registrationPrompted: false,
          registrationCompleted: !!user,
          conversionValue: 0,
          startTime: new Date(),
          lastActiveTime: new Date(),
          deviceType: 'desktop'
        }}
        availableActions={[
          {
            id: 'view_methodology',
            title: '查看分析方法',
            description: '了解这篇分析使用的方法论',
            type: 'primary',
            timing: 'after_reading',
            position: 'floating',
            requiresAuth: false,
            estimatedTime: 10
          },
          {
            id: 'start_practice',
            title: '开始实践',
            description: '使用相同方法分析其他案例',
            type: 'secondary',
            timing: 'on_engagement',
            position: 'floating',
            requiresAuth: true,
            estimatedTime: 30
          }
        ]}
        onActionSelect={handleActionSelect}
        onDismiss={handleActionDismiss}
      />
    </div>
  );
};

export default OfficialAnalysisPreview;